import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Copy, ExternalLink, Calendar } from "lucide-react";
import { toast } from "sonner";
import { OMBELINE_MEETING_EMBED_URL } from "@/components/thales/ombelineMeeting";

const EMAIL = "ombeline@naboo.app";
const SUBJECT = "Demande événementielle Thales";
const OMBELINE_PHOTO =
  "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Logos/ombeline_compressed.jpg";

interface OmbelineContactPopoverProps {
  children: React.ReactNode;
  align?: "start" | "center" | "end";
}

const OmbelineContactPopover: React.FC<OmbelineContactPopoverProps> = ({
  children,
  align = "end",
}) => {
  const [bookOpen, setBookOpen] = useState(false);

  const GMAIL_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    EMAIL
  )}&su=${encodeURIComponent(SUBJECT)}`;
  const OUTLOOK_URL = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(
    EMAIL
  )}&subject=${encodeURIComponent(SUBJECT)}`;

  const copyEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
      } else {
        const ta = document.createElement("textarea");
        ta.value = EMAIL;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Adresse copiée", { description: EMAIL });
    } catch {
      toast.error("Impossible de copier", { description: EMAIL });
    }
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align={align}
          sideOffset={8}
          className="w-[320px] p-0 rounded-2xl border border-black/10 shadow-xl bg-white"
          style={{ zIndex: 100000 }}
        >
          <div className="flex items-center gap-3 p-4 border-b border-black/5">
            <img
              src={OMBELINE_PHOTO}
              alt="Ombeline"
              className="w-11 h-11 rounded-full object-cover"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#111111] truncate">Ombeline</p>
              <p className="text-xs text-[#6b7280] truncate">{EMAIL}</p>
            </div>
          </div>

          <div className="p-3 space-y-2">
            <button
              type="button"
              onClick={() => setBookOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C6E278] text-[#111111] text-sm font-semibold px-3 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
            >
              <Calendar className="w-4 h-4" />
              Prendre RDV
            </button>

            <a
              href={GMAIL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-between gap-2 bg-white border border-black/10 text-[#111111] text-sm font-medium px-3 py-2.5 rounded-xl hover:bg-black/5 transition-colors"
            >
              <span>Envoyer via Gmail</span>
              <ExternalLink className="w-4 h-4 opacity-60" />
            </a>

            <a
              href={OUTLOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-between gap-2 bg-white border border-black/10 text-[#111111] text-sm font-medium px-3 py-2.5 rounded-xl hover:bg-black/5 transition-colors"
            >
              <span>Envoyer via Outlook</span>
              <ExternalLink className="w-4 h-4 opacity-60" />
            </a>

            <button
              type="button"
              onClick={copyEmail}
              className="w-full inline-flex items-center justify-between gap-2 bg-white border border-black/10 text-[#111111] text-sm font-medium px-3 py-2.5 rounded-xl hover:bg-black/5 transition-colors"
            >
              <span>Copier l'adresse</span>
              <Copy className="w-4 h-4 opacity-60" />
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent
          className="max-w-4xl w-[95vw] h-[95vh] sm:h-[760px] p-0 gap-0 overflow-hidden"
          style={{ zIndex: 100000 }}
        >
          <DialogTitle className="sr-only">Prendre rendez-vous avec Ombeline</DialogTitle>
          <iframe
            src={OMBELINE_MEETING_EMBED_URL}
            title="Prendre rendez-vous avec Ombeline"
            className="w-full h-full border-0"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OmbelineContactPopover;
